/**
 * Re-running attribution.
 *
 * `computeBrinsonAttribution` is the only write in the attribution surface: it
 * recomputes the requested horizon × bucketing windows against live positions
 * and stores each result, so a later `getBrinsonAttribution` finds it under
 * `stored` instead of `pending`. It returns how many rows it wrote, not the
 * rows themselves — the caller refetches to read them back.
 *
 * Passing no horizons or no groups lets the resolver pick its own defaults,
 * which is rarely what a desk means; the screen always sends both.
 */
import { gql } from '@apollo/client';
import { RETURN_ATTRIBUTION_RESULT } from '../queries';

/**
 * Force a fresh return-attribution run and overwrite what is stored.
 *
 * `getReturnAttribution` already computes on a cache miss, so this is not how
 * a first result is obtained — it is how a stale one is replaced, after
 * positions or prices have moved under a window that was already computed.
 * Slow on a cold window: it prices every holding on every day in the range.
 *
 * Returns the computed payload itself, in the same shape as the query, so the
 * screen renders the answer without a second round-trip.
 */
export const CALCULATE_RETURN_ATTRIBUTION = gql`
  mutation CalculateReturnAttribution(
    $portfolioSymbol: String!
    $securityGroupId: Int!
    $benchmarkSymbol: String
    $startDate: String
    $endDate: String
    $frequency: String = "monthly"
  ) {
    calculateReturnAttribution(
      portfolioSymbol: $portfolioSymbol
      securityGroupId: $securityGroupId
      benchmarkSymbol: $benchmarkSymbol
      startDate: $startDate
      endDate: $endDate
      frequency: $frequency
    ) { ${RETURN_ATTRIBUTION_RESULT} }
  }
`;

/**
 * Create the default classification groups — sectors, asset classes and the
 * rest — if they do not exist yet.
 *
 * Idempotent, and the only reason it is on an advisor screen at all: a
 * security group is what defines the attribution buckets, so an environment
 * with none cannot run attribution and has no other way to get them.
 */
export const SETUP_DEFAULT_SECURITY_GROUPS = gql`
  mutation SetupDefaultSecurityGroups {
    setupDefaultSecurityGroups {
      id
      name
      description
      isActive
      isClassificationAggregation
    }
  }
`;

/** `urlId`: the stored run's handle for the agent's report — see RESULT_HEAD in queries/attribution.js. */
export const COMPUTE_BRINSON_ATTRIBUTION = gql`
  mutation ComputeBrinsonAttribution(
    $portfolioSymbol: String!
    $benchmarkSymbol: String
    $horizons: [String!]
    $securityGroupIds: [Int!]
    $asOfDate: Date
  ) {
    computeBrinsonAttribution(
      portfolioSymbol: $portfolioSymbol
      benchmarkSymbol: $benchmarkSymbol
      horizons: $horizons
      securityGroupIds: $securityGroupIds
      asOfDate: $asOfDate
    ) {
      success
      storedCount
      message
      urlId
    }
  }
`;

/**
 * Compute and store a fund's multifactor attribution for every trading day in a
 * window — the write side of `getMfMultifactorAttribution`, which then reads the
 * window back. Books are point-in-time: days before the fund's first holdings
 * disclosure are skipped and counted, not guessed. `urlId` as on RESULT_HEAD
 * in queries/attribution.js.
 */
export const COMPUTE_MF_MULTIFACTOR_ATTRIBUTION = gql`
  mutation ComputeMfMultifactorAttribution($securitySymbols: [String!], $startDate: String, $endDate: String) {
    computeMfMultifactorAttribution(securitySymbols: $securitySymbols, startDate: $startDate, endDate: $endDate) {
      success
      urlId
      message
      startDate
      endDate
      daysInWindow
      fundsComputed
      fundDaysStored
      fundDaysSkippedNoDisclosure
      failed
    }
  }
`;

/**
 * A portfolio's multifactor attribution, computed on demand.
 *
 * Unlike the review runners this one COMPUTES IN THE REQUEST and answers with
 * the run itself, so a caller can show the result without refetching — though
 * the screens here refetch anyway, since the stored read is what every other
 * panel on the tab is drawing from.
 */
export const COMPUTE_MULTIFACTOR_ATTRIBUTION = gql`
  mutation ComputeMultifactorAttribution($portfolioSymbol: String!, $startDate: String, $endDate: String) {
    computeMultifactorAttribution(
      portfolioSymbol: $portfolioSymbol
      startDate: $startDate
      endDate: $endDate
    ) {
      success
      message
      asOfDate
      activeReturn
      factorTotal
      residualReturn
    }
  }
`;
